import React, { useState } from 'react';
import { collection, addDoc, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { ProfileRow, ReviewAttribution } from './ProfileLink';
import { formatRentalDateRange, renderStarRating } from './reviewUtils';
import { getPublicName } from './UserProfile';
import { useToast } from './Toast';

export function ReviewStars({ rating, size = '1rem' }) {
  return (
    <span style={{ color: '#f59e0b', fontSize: size, letterSpacing: '1px' }}>
      {renderStarRating(rating)}
    </span>
  );
}

export function ReviewCard({
  review,
  currentUserId,
  showGear = false,
  showRentalDates = false,
  onDisputeSubmitted,
}) {
  const canDispute =
    currentUserId &&
    (currentUserId === review.reviewedId || currentUserId === review.reviewerId);

  return (
    <div
      style={{
        padding: '18px 0',
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      <div style={{ marginBottom: '8px' }}>
        <ReviewStars rating={review.rating} size="1.1rem" />
        <span style={{ marginLeft: '10px', color: '#6b7280', fontSize: '0.9rem' }}>
          {review.rating || 0}/5
        </span>
      </div>

      {showGear && review.gearName && (
        <p style={{ margin: '0 0 6px 0', color: '#1f2937', fontWeight: '600' }}>
          {review.gearName}
        </p>
      )}

      {showRentalDates && review.rentalStartDate && review.rentalEndDate && (
        <p style={{ margin: '0 0 8px 0', color: '#6b7280', fontSize: '0.9rem' }}>
          Rental: {formatRentalDateRange(review.rentalStartDate, review.rentalEndDate)}
        </p>
      )}

      <p style={{ margin: '0 0 10px 0', color: '#374151', lineHeight: '1.65' }}>
        {review.comment?.trim() || 'No written comment.'}
      </p>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: '0.85rem',
            color: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <span>—</span>
          <ReviewAttribution reviewerId={review.reviewerId} reviewerName={review.reviewerName} />
        </p>

        {canDispute && (
          <DisputeReviewButton review={review} onSubmitted={onDisputeSubmitted} />
        )}
      </div>
    </div>
  );
}

function DisputeReviewButton({ review, onSubmitted }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          padding: '6px 12px',
          backgroundColor: 'transparent',
          color: '#6b7280',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          fontSize: '0.8rem',
          cursor: 'pointer',
        }}
      >
        Report a concern
      </button>
      <DisputeReviewModal
        isOpen={open}
        onClose={() => setOpen(false)}
        review={review}
        onSubmitted={onSubmitted}
      />
    </>
  );
}

export function DisputeReviewModal({ isOpen, onClose, review, onSubmitted }) {
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !review) return null;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      showToast('Please describe your concern so our team can review it.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const submitReviewDispute = httpsCallable(functions, 'submitReviewDispute');
      await submitReviewDispute({
        reviewId: review.id,
        reason: reason.trim(),
      });
      showToast('Your concern has been submitted. Our team will follow up privately by email.', 'success');
      setReason('');
      onSubmitted?.();
      onClose();
    } catch (error) {
      showToast(error.message || 'Could not submit your report. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 5000,
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '520px',
        }}
      >
        <h3 style={{ marginTop: 0 }}>Report a Review Concern</h3>
        <p style={{ color: '#6b7280', lineHeight: '1.6', fontSize: '0.95rem' }}>
          Tell us what seems inaccurate or unfair. Our team will review this privately and reach out
          to the members involved by email. This will not be posted publicly.
        </p>
        <textarea
          placeholder="Describe your concern..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          style={{
            width: '100%',
            minHeight: '120px',
            padding: '12px',
            borderRadius: '10px',
            border: '1px solid #d1d5db',
            marginBottom: '20px',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: submitting ? '#9ca3af' : '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontWeight: '600',
              cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Report'}
          </button>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReviewModal({
  isOpen,
  onClose,
  rental,
  user,
  isOwner,
  existingReviews = [],
  onSubmitted,
}) {
  const { showToast } = useToast();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reviewType = isOwner ? 'owner-review' : 'renter-review';
  const alreadyReviewed =
    rental &&
    user &&
    existingReviews.some(
      (review) =>
        review.rentalId === rental.id &&
        review.type === reviewType &&
        review.reviewerId === user.uid
    );

  const submitReview = async () => {
    if (rating === 0) {
      showToast('Please select a rating (1-5 stars)', 'error');
      return;
    }
    if (alreadyReviewed) {
      showToast('You have already submitted a review for this rental.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      let reviewerName = user.displayName || user.email || 'Member';
      const reviewerSnap = await getDoc(doc(db, 'users', user.uid));
      if (reviewerSnap.exists()) {
        reviewerName = getPublicName(reviewerSnap.data(), reviewerName);
      }

      await addDoc(collection(db, 'reviews'), {
        gearId: rental.gearId,
        gearName: rental.gearName || '',
        reviewerId: user.uid,
        reviewerName,
        reviewedId: isOwner ? rental.renterId : rental.ownerId,
        rentalId: rental.id,
        rentalStartDate: rental.rentalStartDate || '',
        rentalEndDate: rental.rentalEndDate || '',
        rating,
        comment: comment.trim(),
        createdAt: new Date().toISOString(),
        type: reviewType,
      });

      showToast('Thank you! Your review has been submitted.', 'success');
      onSubmitted?.();
      onClose();
    } catch (error) {
      showToast(`Error submitting review: ${error.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !rental) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 4000,
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '16px',
          width: '90%',
          maxWidth: '480px',
        }}
      >
        <h3 style={{ marginTop: 0 }}>
          {isOwner ? 'Rate Your Renter' : `Review ${rental.gearName || 'this gear'}`}
        </h3>
        <ProfileRow
          label={isOwner ? 'Renter' : 'Owner'}
          userId={isOwner ? rental.renterId : rental.ownerId}
        />
        <p style={{ color: '#6b7280', fontSize: '0.92rem' }}>
          Rental dates: {formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)}
        </p>

        {alreadyReviewed ? (
          <p style={{ color: '#047857', fontWeight: '600' }}>You already reviewed this rental.</p>
        ) : (
          <>
            <div style={{ margin: '20px 0', fontSize: '2.2rem' }}>
              {[1, 2, 3, 4, 5].map((star) => (
                <span
                  key={star}
                  onClick={() => setRating(star)}
                  style={{ cursor: 'pointer', color: star <= rating ? '#f59e0b' : '#d1d5db' }}
                >
                  ★
                </span>
              ))}
            </div>

            <textarea
              placeholder={isOwner ? 'How was this renter to work with?' : 'How was the gear and experience?'}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              style={{
                width: '100%',
                height: '120px',
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid #d1d5db',
                marginBottom: '20px',
                boxSizing: 'border-box',
              }}
            />
          </>
        )}

        <div style={{ display: 'flex', gap: '12px' }}>
          {!alreadyReviewed && (
            <button
              type="button"
              onClick={submitReview}
              disabled={submitting}
              style={{
                flex: 1,
                padding: '14px',
                backgroundColor: submitting ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontWeight: '600',
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export function GearRentalHistory({ rentals, reviews, gearName, currentUserId }) {
  if (!rentals.length) {
    return <p style={{ color: '#6b7280' }}>No completed rentals to show yet.</p>;
  }

  return (
    <div style={{ display: 'grid', gap: '16px' }}>
      {rentals.map((rental) => {
        const review = reviews.find(
          (item) => item.rentalId === rental.id && item.type === 'renter-review'
        );

        return (
          <div
            key={rental.id}
            style={{
              padding: '18px 20px',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              backgroundColor: '#f9fafb',
            }}
          >
            <p style={{ margin: '0 0 10px 0', color: '#1f2937', lineHeight: '1.6' }}>
              <strong>{gearName}</strong> rented{' '}
              {formatRentalDateRange(rental.rentalStartDate, rental.rentalEndDate)} by{' '}
              <ReviewAttribution reviewerId={rental.renterId} reviewerName={rental.renterName} />
            </p>

            {review ? (
              <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb' }}>
                <ReviewStars rating={review.rating} />
                <p style={{ margin: '8px 0 0 0', color: '#374151', lineHeight: '1.6' }}>
                  {review.comment?.trim() || 'No written comment.'}
                </p>
                {currentUserId &&
                  (currentUserId === review.reviewerId || currentUserId === review.reviewedId) && (
                    <div style={{ marginTop: '10px' }}>
                      <DisputeReviewButton review={review} />
                    </div>
                  )}
              </div>
            ) : (
              <p style={{ margin: '8px 0 0 0', color: '#9ca3af', fontSize: '0.9rem' }}>
                No renter review submitted for this rental yet.
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}